import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import {
  Plus,
  Trash2,
  ChevronDown,
  ChevronRight,
  UsersRound,
  Mail,
  Phone,
  MoreHorizontal,
  Copy,
  UserMinus,
  UserCheck,
  Coins,
  UserCog,
  Check,
  Pencil,
} from "lucide-react";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "../components/ui/hover-card";
import { Checkbox } from "../components/ui/checkbox";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

const ASSIGN_GRID = "1.4fr 1.4fr 1.6fr 60px";

const ROLES = ["Owner", "Admin", "Member", "Viewer"];

const ROLE_STYLES = {
  Owner: "bg-amber-50 text-amber-800 border-amber-200",
  Admin: "bg-violet-50 text-violet-800 border-violet-200",
  Member: "bg-sky-50 text-sky-800 border-sky-200",
  Viewer: "bg-slate-50 text-slate-700 border-slate-200",
};

// Soft pastel chip palette — agent name → color class. Hash-based so same agent always renders the same color.
const CHIP_PALETTE = [
  "bg-violet-100 text-violet-800 border-violet-200",
  "bg-rose-100 text-rose-800 border-rose-200",
  "bg-sky-100 text-sky-800 border-sky-200",
  "bg-amber-100 text-amber-800 border-amber-200",
  "bg-emerald-100 text-emerald-800 border-emerald-200",
  "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-200",
  "bg-cyan-100 text-cyan-800 border-cyan-200",
  "bg-orange-100 text-orange-800 border-orange-200",
];
function chipColor(name) {
  if (!name) return CHIP_PALETTE[0];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return CHIP_PALETTE[h % CHIP_PALETTE.length];
}

function ContactDialog({ open, onOpenChange, mode, value, onSave }) {
  const [v, setV] = useState("");
  useEffect(() => { if (open) setV(value || ""); }, [open, value]);
  const isEmail = mode === "email";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid={`contact-dialog-${mode}`}>
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            {isEmail ? <Mail className="h-5 w-5 text-blue-600" /> : <Phone className="h-5 w-5 text-emerald-600" />}
            {isEmail ? "Email" : "Phone"}
          </DialogTitle>
          <DialogDescription>{isEmail ? "Add or update the email address." : "Add or update the phone number."}</DialogDescription>
        </DialogHeader>
        <Input
          value={v}
          onChange={(e) => setV(e.target.value)}
          placeholder={isEmail ? "name@example.com" : "+91 98765 43210"}
          data-testid={`contact-input-${mode}`}
          autoFocus
        />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={async () => { await onSave(v.trim()); onOpenChange(false); toast.success("Saved"); }} className="btn-brand" data-testid={`contact-save-${mode}`}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddCreditsDialog({ open, onOpenChange, currentCredits, userName, onConfirm }) {
  const [amount, setAmount] = useState("");
  useEffect(() => { if (open) setAmount(""); }, [open]);
  const parsed = Number(amount);
  const valid = amount !== "" && !Number.isNaN(parsed);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="wu-add-credits-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Coins className="h-5 w-5 text-amber-600" /> Add credits
          </DialogTitle>
          <DialogDescription>
            Bump credits for <span className="font-medium text-slate-800">{userName || "this user"}</span>. Current balance:{" "}
            <span className="font-mono text-slate-800">{currentCredits ?? 0}</span>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <label className="text-xs font-medium text-slate-600">Credits to add</label>
          <Input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="e.g. 100"
            data-testid="wu-add-credits-input"
            autoFocus
          />
          {valid && (
            <p className="text-xs text-slate-500">
              New balance: <span className="font-mono font-semibold text-slate-800">{(Number(currentCredits) || 0) + parsed}</span>
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={async () => {
              if (!valid) return;
              await onConfirm(parsed);
              onOpenChange(false);
            }}
            disabled={!valid}
            className="btn-brand"
            data-testid="wu-add-credits-confirm"
          >
            Add
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ContactIconButton({ mode, value, onEdit, wuId }) {
  // mode: "email" | "phone"
  const [copied, setCopied] = useState(false);
  const isEmail = mode === "email";
  const Icon = isEmail ? Mail : Phone;
  const filled = !!value;
  const filledClasses = isEmail
    ? "bg-sky-50 text-blue-700 hover:bg-sky-100 border-sky-200"
    : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border-emerald-200";
  const emptyClasses = "bg-slate-50 text-slate-400 hover:text-slate-600 hover:bg-slate-100 border-dashed border-slate-300";

  const handleCopy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success(`${isEmail ? "Email" : "Phone"} copied`);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Copy failed");
    }
  };

  const trigger = (
    <button
      onClick={onEdit}
      className={`h-8 w-8 rounded-md border flex items-center justify-center transition-colors ${filled ? filledClasses : emptyClasses}`}
      title={filled ? value : `Add ${isEmail ? "email" : "phone"}`}
      data-testid={`wu-${mode}-${wuId}`}
      aria-label={filled ? `${isEmail ? "Email" : "Phone"}: ${value}` : `Add ${mode}`}
    >
      <Icon className="h-3.5 w-3.5" />
    </button>
  );

  if (!filled) return trigger;

  return (
    <HoverCard openDelay={120} closeDelay={80}>
      <HoverCardTrigger asChild>{trigger}</HoverCardTrigger>
      <HoverCardContent
        align="center"
        side="top"
        sideOffset={6}
        className="w-auto max-w-xs p-2"
        data-testid={`wu-${mode}-hover-${wuId}`}
      >
        <div className="flex items-center gap-2">
          <Icon className={`h-3.5 w-3.5 shrink-0 ${isEmail ? "text-blue-600" : "text-emerald-600"}`} />
          <span className="text-xs font-medium text-slate-800 truncate" data-testid={`wu-${mode}-hover-value-${wuId}`}>
            {value}
          </span>
          <button
            onClick={handleCopy}
            className="h-6 w-6 rounded-md hover:bg-slate-100 text-slate-500 hover:text-slate-800 flex items-center justify-center shrink-0"
            title="Copy"
            data-testid={`wu-${mode}-copy-${wuId}`}
          >
            {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(); }}
            className="h-6 w-6 rounded-md hover:bg-slate-100 text-slate-500 hover:text-slate-800 flex items-center justify-center shrink-0"
            title="Edit"
            data-testid={`wu-${mode}-edit-${wuId}`}
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}


function AgentChipsPicker({ allAgents, selectedAgentNames, onToggle, disabled, wuId }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const agentNames = useMemo(
    () => Array.from(new Set(allAgents.map((a) => a.agent_name).filter(Boolean))).sort(),
    [allAgents],
  );
  const filtered = useMemo(
    () => (query ? agentNames.filter((n) => n.toLowerCase().includes(query.toLowerCase())) : agentNames),
    [agentNames, query],
  );

  const visibleChips = selectedAgentNames.slice(0, 2);
  const overflow = selectedAgentNames.length - visibleChips.length;

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQuery(""); }}>
      <PopoverTrigger asChild>
        <button
          disabled={disabled}
          className={`h-8 min-w-[140px] max-w-[260px] px-2 rounded-md border flex items-center gap-1 transition-colors text-left ${
            selectedAgentNames.length
              ? "bg-white border-slate-200 hover:border-blue-300"
              : "bg-slate-50 border-dashed border-slate-300 hover:border-blue-300 hover:bg-white"
          } disabled:opacity-60 disabled:cursor-not-allowed`}
          data-testid={`wu-chips-trigger-${wuId}`}
          title={selectedAgentNames.length ? `${selectedAgentNames.length} agent${selectedAgentNames.length === 1 ? "" : "s"}` : "Pick agents"}
        >
          {selectedAgentNames.length === 0 ? (
            <span className="text-xs text-slate-400 flex items-center gap-1">
              <Plus className="h-3.5 w-3.5" /> Pick agents
            </span>
          ) : (
            <div className="flex items-center gap-1 overflow-hidden flex-1">
              {visibleChips.map((n) => (
                <span
                  key={n}
                  className={`text-[11px] font-medium px-1.5 py-0.5 rounded border truncate max-w-[90px] ${chipColor(n)}`}
                >
                  {n}
                </span>
              ))}
              {overflow > 0 && (
                <span className="text-[11px] font-medium px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                  +{overflow}
                </span>
              )}
            </div>
          )}
          <ChevronDown className="h-3.5 w-3.5 text-slate-400 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start" data-testid={`wu-chips-popover-${wuId}`}>
        <div className="p-2 border-b border-slate-100">
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search agents…"
            className="h-8 text-xs"
            data-testid={`wu-chips-search-${wuId}`}
            autoFocus
          />
        </div>
        <div className="max-h-64 overflow-y-auto py-1">
          {filtered.length === 0 ? (
            <div className="px-3 py-4 text-center text-xs text-slate-400">No agents found.</div>
          ) : (
            filtered.map((n) => {
              const checked = selectedAgentNames.includes(n);
              return (
                <div
                  key={n}
                  role="option"
                  tabIndex={0}
                  onClick={() => onToggle(n)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggle(n); } }}
                  className="w-full flex items-center gap-2 px-2 py-1.5 hover:bg-slate-50 cursor-pointer focus:bg-slate-50 focus:outline-none"
                  data-testid={`wu-chips-option-${wuId}-${n}`}
                  aria-selected={checked}
                >
                  <Checkbox checked={checked} className="pointer-events-none" />
                  <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded border ${chipColor(n)}`}>{n}</span>
                </div>
              );
            })
          )}
        </div>
        <div className="px-3 py-2 border-t border-slate-100 bg-slate-50/60 text-[11px] text-slate-500 leading-snug">
          <span className="font-medium text-slate-700">Default permission applied</span> to selected agents. Override per agent / sub-agent below.
        </div>
      </PopoverContent>
    </Popover>
  );
}

function OverridesTable({ overrides, chipAgentNames, agents, templates, disabled, onUpdate, onAdd, onRemove }) {
  const subAgentsByName = useMemo(() => {
    const m = new Map();
    for (const a of agents) {
      if (!a.agent_name) continue;
      if (!m.has(a.agent_name)) m.set(a.agent_name, new Set());
      if (a.sub_agent) m.get(a.agent_name).add(a.sub_agent);
    }
    const out = new Map();
    for (const [k, v] of m) out.set(k, Array.from(v).sort());
    return out;
  }, [agents]);
  const templatesByAgent = useMemo(() => {
    const m = new Map();
    for (const t of templates) {
      if (!m.has(t.agent_name)) m.set(t.agent_name, []);
      m.get(t.agent_name).push(t);
    }
    return m;
  }, [templates]);

  const noChips = chipAgentNames.length === 0;

  return (
    <div className="rounded-xl border border-slate-200 overflow-hidden bg-white">
      <div className="px-3 py-2 bg-slate-50 border-b border-slate-200">
        <p className="text-[11px] text-slate-600 leading-snug">
          <span className="font-semibold text-slate-800">Overrides.</span> In case you want to override or apply special permission on any agent / sub-agent, add a row below. Only agents picked above are listed.
        </p>
      </div>
      <div
        className="grid items-center gap-2 px-3 h-10 bg-slate-100/70 border-b border-slate-200 text-[11px] font-bold uppercase tracking-[0.12em] text-slate-700"
        style={{ gridTemplateColumns: ASSIGN_GRID }}
      >
        <div>Agent</div>
        <div>Sub-agent</div>
        <div>Permission template</div>
        <div></div>
      </div>
      {overrides.length === 0 ? (
        <div className="px-3 py-5 text-center text-xs text-slate-400">
          {noChips ? 'Pick agents above first, then add overrides if needed.' : 'No overrides — defaults apply for all picked agents.'}
        </div>
      ) : (
        overrides.map((a) => {
          const subs = subAgentsByName.get(a.agent_name) || [];
          const tpls = templatesByAgent.get(a.agent_name) || [];
          return (
            <div
              key={a.id}
              className="grid items-center gap-2 px-3 h-12 border-b border-slate-100 hover:bg-slate-50/40 group"
              style={{ gridTemplateColumns: ASSIGN_GRID }}
              data-testid={`wu-assign-${a.id}`}
            >
              <Select
                value={a.agent_name || "__pick__"}
                onValueChange={(v) => onUpdate(a.id, { agent_name: v, sub_agent: "", permission_group_id: "" })}
                disabled={disabled}
              >
                <SelectTrigger className="h-8 text-xs" data-testid={`wu-assign-agent-${a.id}`}><SelectValue placeholder="Pick agent" /></SelectTrigger>
                <SelectContent>
                  {chipAgentNames.length === 0 ? (
                    <div className="px-2 py-1.5 text-xs text-slate-400">Pick agents above first.</div>
                  ) : (
                    chipAgentNames.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)
                  )}
                </SelectContent>
              </Select>
              <Select
                value={a.sub_agent || "__pick__"}
                onValueChange={(v) => onUpdate(a.id, { sub_agent: v === "__any__" ? "" : v })}
                disabled={disabled || !a.agent_name}
              >
                <SelectTrigger className="h-8 text-xs" data-testid={`wu-assign-sub-${a.id}`}><SelectValue placeholder={a.agent_name ? "Pick sub-agent" : "Pick agent first"} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__any__"><span className="text-slate-500">— any sub-agent</span></SelectItem>
                  {subs.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select
                value={a.permission_group_id || "__pick__"}
                onValueChange={(v) => onUpdate(a.id, { permission_group_id: v === "__none__" ? "" : v })}
                disabled={disabled || !a.agent_name}
              >
                <SelectTrigger className="h-8 text-xs" data-testid={`wu-assign-tpl-${a.id}`}><SelectValue placeholder={a.agent_name ? "Pick template" : "Pick agent first"} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__"><span className="text-slate-500">— default</span></SelectItem>
                  {tpls.length === 0 ? (
                    <div className="px-2 py-1.5 text-xs text-slate-400">No templates for {a.agent_name}. Create one in Permissions.</div>
                  ) : (
                    tpls.map((t) => <SelectItem key={t.id} value={t.id}>{t.template_name || "(unnamed)"}</SelectItem>)
                  )}
                </SelectContent>
              </Select>
              <button
                onClick={() => onRemove(a.id)}
                disabled={disabled}
                className="opacity-0 group-hover:opacity-100 h-8 w-8 rounded-md hover:bg-rose-50 text-slate-400 hover:text-rose-600 flex items-center justify-center transition-all disabled:pointer-events-none disabled:opacity-0"
                data-testid={`wu-assign-remove-${a.id}`}
                title="Remove override"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          );
        })
      )}
      <div className="px-3 py-2 border-t border-slate-100">
        <button
          onClick={onAdd}
          disabled={disabled || noChips}
          className="h-7 px-2 rounded-md bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs font-medium flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="wu-assign-add"
          title={noChips ? "Pick agents above first" : "Add an override"}
        >
          <Plus className="h-3.5 w-3.5" /> Add override
        </button>
      </div>
    </div>
  );
}

function WorkspaceUserRow({
  wu,
  agents,
  templates,
  expanded,
  onToggle,
  onUpdate,
  onDelete,
  onCopy,
  onToggleActive,
  onOpenAddCredits,
  onImpersonate,
}) {
  const [contactDlg, setContactDlg] = useState(null);

  const role = wu.role || "Viewer";
  const isActive = wu.is_active !== false;
  const isOwner = role === "Owner";
  const canEditAssignments = role === "Owner" || role === "Admin";

  // Derive chip agents (distinct names from base assignments) and overrides (rows flagged is_override:true)
  const chipAgentNames = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const a of (wu.assignments || [])) {
      if (!a.agent_name || a.is_override) continue;
      if (seen.has(a.agent_name)) continue;
      seen.add(a.agent_name);
      out.push(a.agent_name);
    }
    return out;
  }, [wu.assignments]);
  const overrides = useMemo(
    () => (wu.assignments || []).filter((a) => a.is_override === true),
    [wu.assignments],
  );

  const setName = (val) => {
    if (val !== wu.name) onUpdate(wu.id, { name: val });
  };

  const toggleChip = (agentName) => {
    const list = wu.assignments || [];
    const hasBase = list.some((a) => a.agent_name === agentName && !a.is_override);
    let next;
    if (hasBase) {
      // Remove base entry AND all overrides for this agent
      next = list.filter((a) => a.agent_name !== agentName);
    } else {
      // Add base/default chip entry for this agent
      next = [
        ...list,
        { id: `tmp-${Date.now()}-${Math.random().toString(36).slice(-4)}`, agent_name: agentName, sub_agent: "", permission_group_id: "", is_override: false },
      ];
    }
    onUpdate(wu.id, { assignments: next });
  };

  const updateAssignment = (assignId, patch) => {
    const next = (wu.assignments || []).map((a) => (a.id === assignId ? { ...a, ...patch } : a));
    onUpdate(wu.id, { assignments: next });
  };
  const addOverride = () => {
    const firstAgent = chipAgentNames[0] || "";
    const next = [
      ...(wu.assignments || []),
      { id: `tmp-${Date.now()}-${Math.random().toString(36).slice(-4)}`, agent_name: firstAgent, sub_agent: "", permission_group_id: "", is_override: true },
    ];
    onUpdate(wu.id, { assignments: next });
  };
  const removeAssignment = (assignId) => {
    const next = (wu.assignments || []).filter((a) => a.id !== assignId);
    onUpdate(wu.id, { assignments: next });
  };

  return (
    <div
      className={`rounded-2xl border bg-white transition-opacity ${
        isActive ? "border-slate-200" : "border-slate-200 bg-slate-50/60 opacity-70"
      }`}
      data-testid={`wu-row-${wu.id}`}
    >
      <div className="flex items-center gap-3 px-4 h-14 border-b border-slate-100">
        <button onClick={onToggle} className="text-slate-400 hover:text-slate-700" data-testid={`wu-toggle-${wu.id}`}>
          {expanded ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
        </button>
        <Input
          defaultValue={wu.name}
          onBlur={(e) => setName(e.target.value.trim())}
          placeholder="Full name"
          className="h-9 text-sm font-semibold flex-1 max-w-[240px]"
          data-testid={`wu-name-${wu.id}`}
        />
        <ContactIconButton
          mode="email"
          value={wu.email}
          onEdit={() => setContactDlg("email")}
          wuId={wu.id}
        />
        <ContactIconButton
          mode="phone"
          value={wu.phone}
          onEdit={() => setContactDlg("phone")}
          wuId={wu.id}
        />

        {/* Role dropdown */}
        <Select
          value={role}
          onValueChange={(v) => onUpdate(wu.id, { role: v })}
        >
          <SelectTrigger
            className={`h-8 w-[110px] text-xs font-medium border ${ROLE_STYLES[role] || ROLE_STYLES.Viewer}`}
            data-testid={`wu-role-${wu.id}`}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ROLES.map((r) => (
              <SelectItem key={r} value={r} data-testid={`wu-role-option-${wu.id}-${r.toLowerCase()}`}>{r}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Agents multi-select chips column */}
        <AgentChipsPicker
          allAgents={agents}
          selectedAgentNames={chipAgentNames}
          onToggle={toggleChip}
          disabled={!canEditAssignments}
          wuId={wu.id}
        />

        {/* Credits inline edit */}
        <div className="flex items-center gap-1.5">
          <Coins className="h-3.5 w-3.5 text-amber-500" />
          <Input
            key={`c-${wu.id}-${wu.credits ?? 0}`}
            type="number"
            defaultValue={wu.credits ?? 0}
            onBlur={(e) => {
              const val = Number(e.target.value);
              if (!Number.isNaN(val) && val !== (wu.credits ?? 0)) {
                onUpdate(wu.id, { credits: val });
              }
            }}
            className="h-8 w-[90px] text-xs text-right font-mono"
            data-testid={`wu-credits-${wu.id}`}
            title="Credits (inline edit)"
          />
        </div>

        <span className="text-[11px] px-2 py-0.5 rounded-md bg-slate-100 text-slate-600 hidden xl:inline">
          {chipAgentNames.length} agent{chipAgentNames.length === 1 ? "" : "s"}{overrides.length > 0 ? ` · ${overrides.length} override${overrides.length === 1 ? "" : "s"}` : ""}
        </span>

        {/* 3-dots action menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="h-8 w-8 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 flex items-center justify-center"
              data-testid={`wu-menu-${wu.id}`}
              title="Actions"
            >
              <MoreHorizontal className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuItem
              onClick={() => onCopy(wu)}
              data-testid={`wu-action-copy-${wu.id}`}
            >
              <Copy className="h-4 w-4 mr-2 text-slate-500" /> Copy user
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => onOpenAddCredits(wu)}
              data-testid={`wu-action-add-credits-${wu.id}`}
            >
              <Coins className="h-4 w-4 mr-2 text-amber-600" /> Add credits
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => onImpersonate(wu)}
              data-testid={`wu-action-impersonate-${wu.id}`}
            >
              <UserCog className="h-4 w-4 mr-2 text-violet-600" /> Impersonate user
            </DropdownMenuItem>
            {!isOwner && <DropdownMenuSeparator />}
            {!isOwner && (
              <DropdownMenuItem
                onClick={() => onToggleActive(wu)}
                data-testid={`wu-action-toggle-active-${wu.id}`}
              >
                {isActive ? (
                  <><UserMinus className="h-4 w-4 mr-2 text-amber-600" /> Deactivate user</>
                ) : (
                  <><UserCheck className="h-4 w-4 mr-2 text-emerald-600" /> Activate user</>
                )}
              </DropdownMenuItem>
            )}
            {!isOwner && (
              <DropdownMenuItem
                onClick={() => onDelete(wu)}
                className="text-rose-600 focus:text-rose-700 focus:bg-rose-50"
                data-testid={`wu-action-remove-${wu.id}`}
              >
                <Trash2 className="h-4 w-4 mr-2" /> Remove user
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {expanded && (
        <div className="p-4">
          <OverridesTable
            overrides={overrides}
            chipAgentNames={chipAgentNames}
            agents={agents}
            templates={templates}
            disabled={!canEditAssignments}
            onUpdate={updateAssignment}
            onAdd={addOverride}
            onRemove={removeAssignment}
          />
          {!canEditAssignments && (
            <p className="mt-2 text-[11px] text-slate-400 italic">
              Access config is locked while role is <span className="font-medium">{role}</span>. Promote to Admin/Owner to edit assignments.
            </p>
          )}
        </div>
      )}

      <ContactDialog
        open={contactDlg !== null}
        onOpenChange={(o) => { if (!o) setContactDlg(null); }}
        mode={contactDlg || "email"}
        value={contactDlg === "phone" ? wu.phone : wu.email}
        onSave={async (v) => onUpdate(wu.id, contactDlg === "phone" ? { phone: v } : { email: v })}
      />
    </div>
  );
}

export default function UserSection() {
  const [users, setUsers] = useState([]);
  const [agents, setAgents] = useState([]);
  const [groups, setGroups] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(true);
  const [addCreditsFor, setAddCreditsFor] = useState(null); // wu object or null

  const load = async () => {
    try {
      setLoading(true);
      const [u, a, g] = await Promise.all([
        api.get("/workspace-users"),
        api.get("/agents"),
        api.get("/permission-groups"),
      ]);
      setUsers(u.data);
      setAgents(a.data);
      setGroups(g.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const templates = useMemo(() => groups.filter((g) => g.is_template), [groups]);

  const handleAdd = async () => {
    try {
      const next_order = users.length === 0 ? 0 : Math.max(...users.map((u) => u.order || 0)) + 1;
      const { data } = await api.post("/workspace-users", {
        name: "New person",
        email: "",
        phone: "",
        role: "Member",
        credits: 0,
        is_active: true,
        assignments: [],
        order: next_order,
      });
      setUsers((p) => [...p, data]);
      setExpanded((e) => ({ ...e, [data.id]: true }));
    } catch { toast.error("Add failed"); }
  };

  const handleUpdate = async (id, patch) => {
    setUsers((p) => p.map((u) => (u.id === id ? { ...u, ...patch } : u)));
    try {
      const { data } = await api.patch(`/workspace-users/${id}`, patch);
      setUsers((p) => p.map((u) => (u.id === id ? data : u)));
    } catch { toast.error("Save failed"); load(); }
  };

  const handleDelete = async (wu) => {
    if (!window.confirm(`Remove ${wu.name || "this user"}? This cannot be undone.`)) return;
    try {
      await api.delete(`/workspace-users/${wu.id}`);
      setUsers((p) => p.filter((u) => u.id !== wu.id));
      toast.success("Removed");
    } catch { toast.error("Delete failed"); }
  };

  const handleCopy = async (wu) => {
    try {
      const next_order = Math.max(...users.map((u) => u.order || 0), 0) + 1;
      const payload = {
        name: `${wu.name || "User"} (copy)`,
        email: "",
        phone: "",
        role: wu.role === "Owner" ? "Admin" : (wu.role || "Viewer"),
        credits: wu.credits || 0,
        is_active: true,
        assignments: (wu.assignments || []).map((a) => ({
          id: `tmp-${Date.now()}-${Math.random().toString(36).slice(-4)}`,
          agent_name: a.agent_name || "",
          sub_agent: a.sub_agent || "",
          permission_group_id: a.permission_group_id || "",
        })),
        order: next_order,
      };
      const { data } = await api.post("/workspace-users", payload);
      setUsers((p) => [...p, data]);
      setExpanded((e) => ({ ...e, [data.id]: true }));
      toast.success("User copied");
    } catch { toast.error("Copy failed"); }
  };

  const handleToggleActive = async (wu) => {
    const nextActive = !(wu.is_active !== false);
    await handleUpdate(wu.id, { is_active: nextActive });
    toast.success(nextActive ? "User activated" : "User deactivated");
  };

  const handleImpersonate = (wu) => {
    toast.info(`Impersonation coming soon — would log in as ${wu.name || "user"}`);
  };

  const handleConfirmAddCredits = async (delta) => {
    if (!addCreditsFor) return;
    const current = Number(addCreditsFor.credits) || 0;
    const next = current + delta;
    await handleUpdate(addCreditsFor.id, { credits: next });
    toast.success(`Added ${delta} credits (new balance: ${next})`);
    setAddCreditsFor(null);
  };

  const toggleExpand = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  return (
    <div className="space-y-6" data-testid="user-section-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-semibold tracking-tight text-slate-900 flex items-center gap-2">
            <UsersRound className="h-6 w-6 text-blue-600" /> User Section
          </h2>
          <p className="text-sm text-slate-500">A directory of people. Each person can be granted scoped access via Agent → Sub-agent → Permission template.</p>
        </div>
        <Button onClick={handleAdd} className="btn-brand h-10 rounded-xl" data-testid="wu-add">
          <Plus className="h-4 w-4 mr-1" /> Add person
        </Button>
      </div>

      {loading ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center text-sm text-slate-500">Loading…</div>
      ) : users.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-12 text-center">
          <p className="text-sm text-slate-500">No people yet — click "Add person" to start.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {users
            .slice()
            .sort((a, b) => (a.order || 0) - (b.order || 0))
            .map((u) => (
              <WorkspaceUserRow
                key={u.id}
                wu={u}
                agents={agents}
                templates={templates}
                expanded={!!expanded[u.id]}
                onToggle={() => toggleExpand(u.id)}
                onUpdate={handleUpdate}
                onDelete={handleDelete}
                onCopy={handleCopy}
                onToggleActive={handleToggleActive}
                onOpenAddCredits={(wu) => setAddCreditsFor(wu)}
                onImpersonate={handleImpersonate}
              />
            ))}
        </div>
      )}

      <AddCreditsDialog
        open={addCreditsFor !== null}
        onOpenChange={(o) => { if (!o) setAddCreditsFor(null); }}
        currentCredits={addCreditsFor?.credits ?? 0}
        userName={addCreditsFor?.name}
        onConfirm={handleConfirmAddCredits}
      />
    </div>
  );
}
